/** Admin: platform settings (bounded numbers the server validates) and the go-live checklist. */
import { api } from '../../core/api.js';
import { register } from '../../core/events.js';
import { html } from '../../core/html.js';
import { reload } from '../../app/shell.js';
import { banner, ic, pageHead, tag } from '../../ui/components.js';
import { field } from '../../ui/forms.js';
import { submitForm } from '../../ui/submit.js';
import { toast } from '../../ui/toast.js';

/**
 * key -> [group, label, help, unit]. The server owns the bounds (GET /admin/settings returns them);
 * this table only decides how a setting is labelled. `unit: 'rupees'` is stored as paise.
 */
export const SETTING_META = {
  gst_percent: ['Money', 'GST rate (%)', 'Added on top of the course price after any coupon. Confirm the rate with your CA.', 'percent'],
  deposit_paise: ['Money', 'Freelancer verification deposit (₹)', 'Refundable. Set to 0 to switch the deposit off.', 'rupees'],
  resume_service_paise: ['Money', 'Resume building help price (₹)', 'What a student or freelancer pays for help building their resume. Set to 0 to switch it off.', 'rupees'],
  deposit_hold_days: ['Money', 'Deposit hold period (days)', 'How long a deposit is held before it becomes refundable.', 'days'],
  payout_hold_days: ['Money', 'Payout hold (days)', 'Days a trainer earning waits before it can be paid out.', 'days'],
  milestone_review_days: ['Money', 'Milestone review window (days)', 'A submitted milestone is approved automatically if the client does not respond in this time.', 'days'],
  msg_limit_per_day: ['Messaging', 'Messages per person per day', 'Resets at midnight India time.', 'count'],
  net_limit_per_week: ['Messaging', 'Connection requests per week', 'Limits cold outreach.', 'count'],
  room_open_minutes: ['Classes', 'Room opens before class (minutes)', 'Students can enter the waiting room this long before the start.', 'minutes'],
  recording_retention_days: ['Classes', 'Recording availability (days)', 'Recordings expire for students after this many days.', 'days'],
  final_pass_percent: ['Learning', 'Final assessment pass mark (%)', 'A certificate needs the final assessment at or above this score.', 'percent'],
  match_min_score: ['Matching', 'Minimum match score (%)', 'Candidates below this score are not suggested for a requirement.', 'percent'],
  match_profiles_per_opening: ['Matching', 'Profiles per opening', 'Profiles to share for each person a company asks for. 1 shows 5 profiles for 5 openings; 3 shows 15.', 'count'],
};

const GROUPS = ['Money', 'Messaging', 'Classes', 'Learning', 'Matching'];

/** Settings page value -> stored value. Rupees are stored as paise. */
export const toStored = (key, text) => {
  const unit = SETTING_META[key]?.[3];
  const n = Number(text);
  if (!Number.isFinite(n) || text === '') return NaN;
  return unit === 'rupees' ? Math.round(n * 100) : n;
};

/** Stored value -> what the person edits. */
export const toShown = (key, stored) => (SETTING_META[key]?.[3] === 'rupees' ? stored / 100 : stored);

const settings = {
  path: '/admin/settings',
  roles: ['admin'],
  title: 'Settings',
  async load(ctx) {
    const [s, system] = await Promise.all([api.get('/admin/settings', { signal: ctx.signal }), api.get('/admin/system', { signal: ctx.signal }).catch(() => null)]);
    return { s, system };
  },
  view({ s, system }) {
    const limits = s.limits;
    const keys = Object.keys(s.settings).filter((k) => SETTING_META[k]);
    const checks = system?.checks || [];
    const notReady = checks.filter((c) => !c.ready);
    return html`${pageHead('Settings', 'Changes apply straight away and are recorded in the audit log.')}
      <div class="grid g2" style="align-items:start">
        <form class="card" id="settings-form" data-submit="settings.save" novalidate>
          ${GROUPS.map((group) => {
            const rows = keys.filter((k) => SETTING_META[k][0] === group);
            if (!rows.length) return '';
            return html`<h3 class="mt-s">${group}</h3><div class="f2">${rows.map((k) => {
              const [, label, help, unit] = SETTING_META[k];
              const lim = limits[k] || {};
              return field(`st-${k}`, label, {
                name: k, type: 'number', val: toShown(k, s.settings[k]), inputmode: 'numeric',
                min: lim.min != null ? toShown(k, lim.min) : null, max: lim.max != null ? toShown(k, lim.max) : null,
                help: `${help} Allowed: ${toShown(k, lim.min)} to ${toShown(k, lim.max)}${unit === 'rupees' ? ' rupees' : ''}.`,
                err: 'Enter a whole number inside the allowed range.',
              });
            })}</div>`;
          })}
          <div class="row mt"><button type="submit" class="btn">Save settings</button></div>
        </form>
        <div class="card"><h3>Go-live checklist</h3>
          ${checks.length
            ? html`${notReady.length && system.environment === 'production' ? banner(html`${notReady.length} item${notReady.length === 1 ? '' : 's'} still on a development stand-in.`, 'bad', 'alert') : ''}
              <div class="list sm mt-s">${checks.map((c) => html`<div class="li">${ic(c.ready ? 'check' : 'alert')}<span class="f1">${c.name}</span>${tag(c.ready ? 'Ready' : 'Stand-in', c.ready ? 'ok' : 'wn')}</div>`)}</div>
              <p class="note mt-s">Environment: <b>${system.environment}</b>. Secrets are never shown here.</p>`
            : html`<p class="mu" style="margin:0">The checklist could not be loaded.</p>`}
        </div>
      </div>`;
  },
};

register({
  'settings.save': (form) => submitForm(form, async (values) => {
    const body = {};
    for (const [key, text] of Object.entries(values)) body[key] = toStored(key, text);
    if (Object.values(body).some((n) => !Number.isInteger(n))) throw new Error('Every setting needs a whole number.');
    await api.put('/admin/settings', { body: { values: body } });
    toast('Settings saved', 'check');
    await reload();
  }, { onError: (error) => toast(error.message, 'alert') }),
});

export const routes = [settings];
